/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardFooter,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';

import Breadcrumbs from '@Components/Breadcrumbs';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import service from '@Service';
import { AVAILABLE_TOOLS } from '@Constants/aiTools';
import { OPERATION_META, OPERATION_KEYS } from '@Constants/operations';
import { soulKeys, useSouls } from '@Hooks/useSouls';

const FormSchema = z.object({
  name: z.string().min(1, { message: 'Name is required.' }),
  description: z.string().optional(),
  systemPrompt: z.string().min(1, { message: 'System prompt is required.' }),
  model: z.string().optional(),
  enabledTools: z.array(z.string()).optional(),
  temperature: z.string().optional(),
  maxSteps: z.string().optional(),
  maxTokens: z.string().optional(),
  thinkingBudget: z.string().optional(),
  isDefault: z.boolean(),
  parentSoulId: z.string().optional(),
  operations: z.array(z.string()).optional(),
});

type FormValues = z.infer<typeof FormSchema>;

export default function CreateSoul() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: allSouls } = useSouls();
  const topLevelSouls = (allSouls ?? []).filter((s) => !s.parentSoulId);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      description: '',
      systemPrompt: '',
      model: '',
      enabledTools: [],
      temperature: '',
      maxSteps: '',
      maxTokens: '',
      thinkingBudget: '',
      isDefault: false,
      parentSoulId: '',
      operations: [],
    },
  });

  const onSubmit = async (values: FormValues) => {
    form.clearErrors('root');

    const payload = {
      name: values.name,
      systemPrompt: values.systemPrompt,
      ...(values.description ? { description: values.description } : {}),
      ...(values.model ? { model: values.model } : {}),
      ...(values.enabledTools?.length
        ? { enabledTools: values.enabledTools }
        : {}),
      ...(values.temperature !== '' && values.temperature !== undefined
        ? { temperature: Number(values.temperature) }
        : {}),
      ...(values.maxSteps !== '' && values.maxSteps !== undefined
        ? { maxSteps: Number(values.maxSteps) }
        : {}),
      ...(values.maxTokens !== '' && values.maxTokens !== undefined
        ? { maxTokens: Number(values.maxTokens) }
        : {}),
      ...(values.thinkingBudget !== '' && values.thinkingBudget !== undefined
        ? { thinkingBudget: Number(values.thinkingBudget) }
        : {}),
      isDefault: values.isDefault,
      ...(values.parentSoulId ? { parentSoulId: values.parentSoulId } : {}),
      ...(values.operations?.length ? { operations: values.operations } : {}),
    };

    try {
      await service.createSoul(payload);
      await queryClient.invalidateQueries({ queryKey: soulKeys.all });
      toastSuccess('Soul created successfully.');
      navigate('/agents');
    } catch (error) {
      console.error('Error creating soul:', error);
      form.setError('root', {
        type: 'server',
        message:
          'Unable to create soul. Please check your details and try again.',
      });
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'AI', href: '/agents' },
          { name: 'Agents', href: '/agents' },
          { name: 'Create', href: '/agents/create' },
        ]}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Heading level="h2">Create An Agent</Heading>
          </div>
          <Card>
            <CardContent>
              {rootError && (
                <div className="mb-4">
                  <Alert variant="destructive">
                    <AlertTitle>Something went wrong</AlertTitle>
                    <AlertDescription>{rootError}</AlertDescription>
                  </Alert>
                </div>
              )}

              <div className="space-y-6 py-3">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Concierge Assistant"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="What does this soul do?"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="parentSoulId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Parent Agent</FormLabel>
                      <FormControl>
                        <select
                          className="border-input focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[3px]"
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <option value="">— None (top-level agent) —</option>
                          {topLevelSouls.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormDescription>
                        If set, this agent becomes a "subagent" — invokable from
                        its parent as a tool, and selectable as an AI snippet in
                        flow trigger messages.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="systemPrompt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>System Prompt</FormLabel>
                      <FormControl>
                        <Textarea
                          className="min-h-48"
                          placeholder="You are a helpful assistant..."
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        The instructions that define how this AI soul behaves.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="model"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Model</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. claude-sonnet-5"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Leave blank to use the default model.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="enabledTools"
                  render={() => (
                    <FormItem>
                      <FormLabel>Enabled Tools</FormLabel>
                      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                        {AVAILABLE_TOOLS.map((tool) => (
                          <FormField
                            key={tool}
                            control={form.control}
                            name="enabledTools"
                            render={({ field }) => (
                              <FormItem className="flex items-center gap-2">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(tool)}
                                    onCheckedChange={(checked) => {
                                      const current = field.value ?? [];
                                      field.onChange(
                                        checked
                                          ? [...current, tool]
                                          : current.filter((t) => t !== tool),
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal">
                                  {tool}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="operations"
                  render={() => (
                    <FormItem>
                      <FormLabel>Operations</FormLabel>
                      <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                        {OPERATION_KEYS.map((key) => (
                          <FormField
                            key={key}
                            control={form.control}
                            name="operations"
                            render={({ field }) => (
                              <FormItem className="flex items-center gap-2">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(key)}
                                    onCheckedChange={(checked) => {
                                      const current = field.value ?? [];
                                      field.onChange(
                                        checked
                                          ? [...current, key]
                                          : current.filter((k) => k !== key),
                                      );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm font-normal">
                                  {OPERATION_META[key]?.label ?? key}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <FormField
                    control={form.control}
                    name="temperature"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Temperature</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="2"
                            placeholder="0-2"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="maxSteps"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Steps</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            max="20"
                            placeholder="1-20"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="maxTokens"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Tokens</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            max="32768"
                            placeholder="1-32768"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="thinkingBudget"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Thinking Budget</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1024"
                            max="128000"
                            placeholder="1024-128000"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Token budget for extended thinking (Claude only).
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="isDefault"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked === true)
                            }
                          />
                        </FormControl>
                        <FormLabel>Default Agent</FormLabel>
                      </div>
                      <FormDescription>
                        The default agent will be used when no agent is
                        explicitly selected.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>

            <CardFooter className="flex gap-2">
              <Button
                type="submit"
                variant="primary"
                loading={form.formState.isSubmitting}
              >
                Create
              </Button>
              <Button variant="link" asChild>
                <Link to="/agents">Cancel</Link>
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}
